
import { useState, useCallback, useEffect, useRef } from 'react';
import { Attendee } from '@/types/attendee';
import { useQrCodeParser } from '@/hooks/admin/useQrCodeParser';
import { useAdminToast } from '@/hooks/admin/useAdminToast';
import { getToastVariantForStatus, createStyledToast } from '@/hooks/admin/useToastStyles';

type OnAttendeeFoundFunction = (attendee: Attendee) => void;

export const useAttendanceScanner = (
  attendees: Attendee[],
  recordAction: (actionType: string, adminName: string, details?: string, attendeeCode?: string) => void,
  adminName: string,
  onAttendeeFound: OnAttendeeFoundFunction,
  { toast }: { toast: (props: { title: string; description: string; variant?: "default" | "destructive" | "success" | "info" }) => void },
  refreshAttendees?: () => Promise<Attendee[]>
) => {
  const [isScanning, setIsScanning] = useState(false);
  const [recentScans, setRecentScans] = useState<Set<string>>(new Set());
  const { parseQrData } = useQrCodeParser();
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScanTimeRef = useRef<number>(0);
  
  // Clear scanning state after a timeout
  const clearScanningState = useCallback((delay: number = 3000) => {
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    
    scanTimeoutRef.current = setTimeout(() => {
      setIsScanning(false);
    }, delay);
  }, []);
  
  // Clear the scanning timeout when component unmounts
  useEffect(() => {
    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, []);
  
  // Function to handle QR code scan
  const handleQrScan = useCallback(async (data: string) => {
    if (!data || isScanning) return;
    
    // Throttle scans to prevent duplicate processing
    const now = Date.now();
    if (now - lastScanTimeRef.current < 800) {
      return;
    }
    lastScanTimeRef.current = now;
    
    // Set scanning state to prevent multiple scans
    setIsScanning(true);
    
    try {
      // Attempt to parse the QR code
      const parsedCode = parseQrData(data);
      
      if (!parsedCode) {
        console.error("Invalid QR code format", data);
        toast({
          title: "QR code không hợp lệ",
          description: "Không thể đọc được mã QR. Vui lòng thử lại.",
          variant: "destructive"
        });
        clearScanningState();
        return;
      }
      
      // Check if this code was recently scanned
      if (recentScans.has(parsedCode)) {
        console.log(`QR Code ${parsedCode} was recently scanned. Ignoring.`);
        toast({
          title: "Đã quét gần đây",
          description: `Mã ${parsedCode} vừa được quét. Vui lòng đợi một chút.`,
          variant: "destructive" // Changed from "info" to "destructive" to make it red
        });
        clearScanningState();
        return;
      }
      
      // Add to recent scans
      setRecentScans(prev => {
        const updated = new Set(prev);
        updated.add(parsedCode);
        return updated;
      });
      
      // Remove from recent scans after a delay
      setTimeout(() => {
        setRecentScans(prev => {
          const updated = new Set(prev);
          updated.delete(parsedCode);
          return updated;
        });
      }, 5000);
      
      // Try to find attendee in the state
      let foundAttendee = attendees.find(a => a.code === parsedCode);
      
      // If not found and we have a refresh function, try fetching latest data
      if (!foundAttendee && refreshAttendees) {
        console.log(`Attendee with code ${parsedCode} not found in current state. Refreshing data...`);
        try {
          const refreshedAttendees = await refreshAttendees();
          foundAttendee = refreshedAttendees.find(a => a.code === parsedCode);
        } catch (error) {
          console.error("Error refreshing attendees:", error);
        }
      }
      
      if (foundAttendee) {
        // Record the scan action
        recordAction(
          'SCAN_QR',
          adminName,
          `QR code scanned: ${foundAttendee.name} (${parsedCode})`,
          parsedCode
        );
        
        // Check if already checked in
        if (foundAttendee.attended) {
          // Display red toast when scanning QR for already checked-in attendee
          toast({
            title: "!!! Đã được điểm danh !!!",
            description: `${foundAttendee.name} đã được điểm danh trước đó !!!`,
            variant: "destructive"
          });
          
          // Add wasAlreadyCheckedIn flag when passing to the found handler
          const enhancedAttendee = {
            ...foundAttendee,
            wasAlreadyCheckedIn: true
          };
          
          // Still pass to the found handler for UI feedback, but with the flag
          onAttendeeFound(enhancedAttendee);
        } else {
          // Pass to the found handler for confirmation
          onAttendeeFound(foundAttendee);
        }
      } else {
        // Attendee not found
        console.error(`Attendee with code ${parsedCode} not found`);
        toast({
          title: "Không tìm thấy",
          description: `Không tìm thấy người tham dự với mã ${parsedCode}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error processing QR code:", error);
      toast({
        title: "Lỗi xử lý",
        description: "Đã xảy ra lỗi khi xử lý mã QR. Vui lòng thử lại.",
        variant: "destructive"
      });
    } finally {
      clearScanningState();
    }
  }, [isScanning, parseQrData, recentScans, attendees, recordAction, adminName, onAttendeeFound, toast, refreshAttendees, clearScanningState]);
  
  // Function to handle manual code submission
  const handleCodeSubmit = useCallback(async (code: string) => {
    if (!code || isScanning) return;
    
    // Validate code format
    const cleanCode = code.trim().toUpperCase();
    
    // Should be something like "LHPxxxxx"
    if (!cleanCode.match(/^LHP[-]?[A-Z0-9]+$/)) {
      toast({
        title: "Mã không hợp lệ",
        description: "Mã phải bắt đầu bằng 'LHP' và theo sau là chữ số/chữ cái.",
        variant: "destructive"
      });
      return;
    }
    
    // Set scanning state to prevent multiple submissions
    setIsScanning(true);
    
    try {
      // Try to find attendee
      let foundAttendee = attendees.find(a => a.code === cleanCode);
      
      // If not found and we have a refresh function, try fetching latest data
      if (!foundAttendee && refreshAttendees) {
        console.log(`Attendee with code ${cleanCode} not found in current state. Refreshing data...`);
        try {
          const refreshedAttendees = await refreshAttendees();
          foundAttendee = refreshedAttendees.find(a => a.code === cleanCode);
        } catch (error) {
          console.error("Error refreshing attendees:", error);
        }
      }
      
      if (foundAttendee) {
        // Record the manual submission action
        recordAction(
          'MANUAL_CODE',
          adminName,
          `Manual code entered: ${foundAttendee.name} (${cleanCode})`,
          cleanCode
        );
        
        // Check if already checked in
        if (foundAttendee.attended) {
          // Display red toast when entering code for already checked-in attendee
          toast({
            title: "!!! Đã được điểm danh !!!",
            description: `${foundAttendee.name} đã được điểm danh trước đó !!!`,
            variant: "destructive"
          });
          
          // Add wasAlreadyCheckedIn flag when passing to the found handler
          const enhancedAttendee = {
            ...foundAttendee,
            wasAlreadyCheckedIn: true
          };
          
          // Still pass to the found handler for UI feedback, but with the flag
          onAttendeeFound(enhancedAttendee);
        } else {
          // Pass to the found handler for confirmation
          onAttendeeFound(foundAttendee);
        }
      } else {
        // Attendee not found
        console.error(`Attendee with code ${cleanCode} not found`);
        toast({
          title: "Không tìm thấy",
          description: `Không tìm thấy người tham dự với mã ${cleanCode}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error processing manual code:", error);
      toast({
        title: "Lỗi xử lý",
        description: "Đã xảy ra lỗi khi xử lý mã. Vui lòng thử lại.",
        variant: "destructive"
      });
    } finally {
      clearScanningState();
    }
  }, [isScanning, attendees, recordAction, adminName, onAttendeeFound, toast, refreshAttendees, clearScanningState]);
  
  // Function for testing/debugging - simulates scanning a QR code
  const simulateQrScan = useCallback((code: string) => {
    handleQrScan(`LHP-${code}`);
  }, [handleQrScan]);
  
  return {
    handleQrScan,
    handleCodeSubmit,
    isScanning,
    simulateQrScan
  };
};
